<template>
  <BasePhoneRow class="contact-list-item" opens-dialog @open="$emit('open')">
    <template #lead>
      <span class="contact-list-item__icon" aria-hidden="true">
        <BaseIcon :name="isOrganization ? 'briefcase' : 'user'" />
      </span>
    </template>
    <template #title>
      <span class="base-phone-row__title contact-list-item__name">{{ name }}</span>
    </template>

    <p v-for="(line, index) in lines" :key="index" class="base-phone-row__line contact-list-item__line">{{ line }}</p>

    <template v-if="result.score != null" #actions>
      <BaseTag class="contact-list-item__score" preset="soft" :tone="scoreTone" role="img" :aria-label="scoreLabel">
        {{ result.score.toFixed(0) }}
      </BaseTag>
    </template>
  </BasePhoneRow>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BasePhoneRow from '@/components/base/BasePhoneRow.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import contactDirectoryScoreMixin from '@/mixins/contact-directory-score-mixin';
import { getContactDirectoryScoreTone } from '@/util/tag-tones/contact-directory-score-tag';

// One result of the contact directory on a phone: a person or an organization, and how to
// reach them. The rest is in its details sheet.
export default {
  name: 'ContactListItem',
  mixins: [contactDirectoryScoreMixin],
  components: { BaseIcon, BasePhoneRow, BaseTag },
  props: {
    result: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isOrganization() {
      return this.result.type === 'ACCOUNT';
    },
    name() {
      return this.isOrganization ? this.result.organization.name : this.result.contactPoint.name;
    },
    // A person: their organization, then how to reach them. An organization: its address and
    // how many people it lists.
    lines() {
      const { organization, contactPoint } = this.result;
      if (this.isOrganization) {
        const people = (organization.contactPoints || []).length;
        return [
          organization.address && organization.address.raw,
          people ? this.$tc('page.contact-directory.phone-list.people', people, { count: people }) : '',
        ].filter(Boolean);
      }
      return [organization && organization.name, contactPoint.email || contactPoint.telephone].filter(Boolean);
    },
    scoreTone() {
      return getContactDirectoryScoreTone(this.result.score);
    },
    scoreLabel() {
      return `Score : ${this.$t(`page.contact-directory.score.${this.getScoreClass(this.result.score)}`)}`;
    },
  },
};
</script>

<style lang="scss">
// The row is BasePhoneRow's; here, what only a contact shows.
.contact-list-item.base-phone-row.has-lead {
  gap: 12px;
  padding: 12px 12px 12px 16px;
}

.contact-list-item {
  &__icon {
    display: inline-flex;
    flex: none;
    margin-top: 2px;
    color: $color-slate-500;

    .base-icon {
      width: 16px;
      height: 16px;
    }
  }

  // The score only tells: a tap on it opens the row, as anywhere else.
  .base-phone-row__actions {
    pointer-events: none;
  }
}
</style>
